import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { api } from "../api";
import { PageHead, Filters, Kpi, Panel, LoadingBlock } from "../shell";
import { money, pct, growthClass, achievementSeverity } from "../lib/format";

export function Sellers({ dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ region_id: "", team_id: "" });
  const [org, setOrg] = useState({ regions: [], teams: [] });
  const navigate = useNavigate();

  useEffect(() => { api.get("/organization").then((r) => setOrg(r.data)); }, []);
  useEffect(() => {
    setData(null);
    const p = { ...periodParams };
    if (filters.region_id) p.region_id = filters.region_id;
    if (filters.team_id) p.team_id = filters.team_id;
    api.get("/analytics/sellers", { params: p }).then((r) => setData(r.data));
  }, [period, year, startDate, endDate, filters]);

  return (
    <main className="content">
      <PageHead eyebrow="PERFORMANS" title="Satıcı Performansı" desc="Satıcı bazında üretim, komisyon, hedef ve YoY karşılaştırması." />
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"} />
      <div className="advanced-filters">
        <label>Bölge<select data-testid="sellers-region-filter" value={filters.region_id} onChange={(e) => setFilters({ ...filters, region_id: e.target.value, team_id: "" })}>
          <option value="">Tümü</option>{org.regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select></label>
        <label>Ekip<select data-testid="sellers-team-filter" value={filters.team_id} onChange={(e) => setFilters({ ...filters, team_id: e.target.value })}>
          <option value="">Tümü</option>
          {org.teams.filter((t) => !filters.region_id || t.region_id === filters.region_id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select></label>
      </div>
      {!data ? <LoadingBlock/> : (
        <div className="panel table-panel">
          <div className="period-caption" data-testid="sellers-period-label">{data.period} · {data.items.length} satıcı</div>
          <table data-testid="sellers-table">
            <thead>
              <tr>
                <th>Satıcı</th><th>Bölge</th><th>Ekip</th>
                <th style={{textAlign:"right"}}>Net üretim</th>
                <th style={{textAlign:"right"}}>Komisyon</th>
                <th style={{textAlign:"right"}}>Efektif %</th>
                <th style={{textAlign:"right"}}>Dönem hedefi</th>
                <th>Gerçekleşme</th>
                <th style={{textAlign:"right"}}>GY komisyon</th>
                <th>Büyüme</th>
                <th style={{textAlign:"right"}}>Yeni İş</th>
                <th style={{textAlign:"right"}}>Yenileme</th><th style={{textAlign:"right"}}>İnceleme</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id} data-testid={`seller-row-${s.id}`} className="clickable" onClick={() => navigate(`/sellers/${s.id}`)}>
                  <td><strong>{s.name}</strong></td>
                  <td>{s.region}</td><td>{s.team}</td>
                  <td style={{textAlign:"right"}}>{money(s.premium, true)}</td>
                  <td style={{textAlign:"right"}}>{money(s.commission, true)}</td>
                  <td style={{textAlign:"right"}}>{pct(s.effective_rate, 2)}</td>
                  <td style={{textAlign:"right"}}>{money(s.target, true)}</td>
                  <td><span className={"pill " + achievementSeverity(s.achievement)}>{pct(s.achievement)}</span></td>
                  <td style={{textAlign:"right"}}>{money(s.previous, true)}</td>
                  <td className={growthClass(s.growth)}>{s.growth !== null ? pct(s.growth) : "—"}</td>
                  <td style={{textAlign:"right"}}>{money(s.new_business, true)}</td>
                  <td style={{textAlign:"right"}}>{money(s.renewal, true)}</td>
                  <td style={{textAlign:"right"}}>{money(s.review, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

export function SellerDetail({ dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    setData(null);
    api.get(`/analytics/sellers/${id}`, { params: periodParams }).then((r) => setData(r.data));
  }, [id, period, year, startDate, endDate]);
  if (!data) return <main className="content"><LoadingBlock/></main>;
  const k = data.kpis;
  return (
    <main className="content">
      <button className="back-link" onClick={() => navigate("/sellers")}><ArrowLeft size={14}/> Satıcı listesi</button>
      <PageHead eyebrow="SATICI DETAYI" title={data.seller.seller_name}
        desc={`${data.seller.region_name || ""} · ${data.seller.team_name || ""} · Satıcı kodu ${data.seller.seller_code || "—"}`}
        actions={<button data-testid="seller-policies-drilldown" className="outline-button" onClick={() => navigate(`/policies?all_dates=1&seller_id=${encodeURIComponent(id)}`)}>Tüm Poliçeleri</button>} />
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"} />
      <div className="period-caption">{data.period}</div>
      <section className="kpi-grid">
        <Kpi label="Net üretim" value={money(k.premium, true)} sub={`${k.policy_count} hareket`} testid="detail-kpi-premium"/>
        <Kpi label="Komisyon" value={money(k.commission, true)} sub={`Efektif ${pct(k.effective_rate, 2)}`} testid="detail-kpi-commission"/>
        <Kpi label="Dönem hedefi" value={money(k.target, true)}
          change={pct(k.achievement)} changeType={k.achievement >= 100 ? "positive" : k.achievement >= 90 ? "neutral" : "negative"}
          sub={`Yıllık hedef ${money(k.annual_target, true)}`} testid="detail-kpi-target"/>
        <Kpi label="GY komisyon" value={money(k.previous, true)}
          change={k.growth !== null ? pct(k.growth) : "—"} changeType={growthClass(k.growth)} testid="detail-kpi-prev"/>
      </section>
      <section className="chart-grid">
        <Panel title="Aylık üretim & komisyon" sub={data.period} className="chart-panel wide">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf4" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }} tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
              <Tooltip formatter={(v) => money(v)} />
              <Line dataKey="premium" stroke="#2d6cdf" strokeWidth={2.5} dot={false} name="Prim" />
              <Line dataKey="commission" stroke="#164a43" strokeWidth={2.5} dot={false} name="Komisyon" />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Poliçe grubu kırılımı" sub="Net üretim" className="chart-panel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.groups.slice(0, 8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e8edf4" />
              <XAxis type="number" hide/>
              <YAxis dataKey="name" type="category" width={82} axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#526174" }}/>
              <Tooltip formatter={(v) => money(v)}/>
              <Bar dataKey="premium" fill="#2d6cdf" barSize={16} radius={[0, 3, 3, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </section>
      <section className="classification-kpis seller-classification-kpis">
        <div data-testid="seller-new-business-premium" className="classification-kpi"><span>Yeni İş Net Prim</span><strong>{money(k.new_business_premium, true)}</strong><small>{pct(k.new_business_pct)} pay</small></div>
        <div data-testid="seller-renewal-premium" className="classification-kpi"><span>Yenileme Net Prim</span><strong>{money(k.renewal_premium, true)}</strong><small>{pct(k.renewal_pct)} pay</small></div>
        <div data-testid="seller-review-premium" className="classification-kpi"><span>İnceleme / Belirsiz</span><strong>{money(k.review_premium, true)}</strong><small>Toplam net prim paydasında</small></div>
      </section>
      <section className="lower-grid">
        <Panel title="Sigorta şirketi dağılımı">
          <table data-testid="seller-companies-table">
            <thead><tr><th>Şirket</th><th style={{textAlign:"right"}}>Prim</th><th style={{textAlign:"right"}}>Komisyon</th></tr></thead>
            <tbody>{data.companies.slice(0, 10).map((c) => (
              <tr key={c.name}><td><strong>{c.name}</strong></td><td style={{textAlign:"right"}}>{money(c.premium, true)}</td><td style={{textAlign:"right"}}>{money(c.commission, true)}</td></tr>
            ))}</tbody>
          </table>
        </Panel>
        <Panel title="Yeni iş / Yenileme">
          <table data-testid="seller-renewal-table">
            <thead><tr><th>Tip</th><th style={{textAlign:"right"}}>Net prim</th><th style={{textAlign:"right"}}>Komisyon</th><th style={{textAlign:"right"}}>Poliçe</th></tr></thead>
            <tbody>{data.renewal.map((r) => (
              <tr key={r.name}><td><strong>{r.name}</strong></td><td style={{textAlign:"right"}}>{money(r.premium, true)}</td><td style={{textAlign:"right"}}>{money(r.commission, true)}</td><td style={{textAlign:"right"}}>{r.policy_count}</td></tr>
            ))}</tbody>
          </table>
        </Panel>
      </section>
    </main>
  );
}
